import { useState, useCallback } from "react";
import { useSessionStorage } from "usehooks-ts";
import { ProductionFormStateWithFrontEndOnlyFields } from "../pages/pm-grading/utilities";

/**
 * Custom hook for managing PM Grading form data
 */
export const usePMGradingForm = () => {
  // Use a record to store form data for each tab separately
  const [formDataByTab, setFormDataByTab] = useSessionStorage<
    Record<string, Partial<ProductionFormStateWithFrontEndOnlyFields>>
  >("PMUIEditSimpleForms", {});

  // Current form data for the active tab
  const [simpleForm, setSimpleForm] = useState<
    Partial<ProductionFormStateWithFrontEndOnlyFields>
  >({});

  // Initialize form data for a specific tab
  const initializeFormData = useCallback(
    (
      tabId: string,
      initialData: Partial<ProductionFormStateWithFrontEndOnlyFields>
    ) => {
      setFormDataByTab((prev) => ({
        ...prev,
        [tabId]: initialData,
      }));

      // If this is the current form being edited, update simpleForm too
      setSimpleForm(initialData);
    },
    [setFormDataByTab]
  );

  // Update form data for the current tab
  const updateFormField = useCallback(
    (
      updater: (
        prev: Partial<ProductionFormStateWithFrontEndOnlyFields>
      ) => Partial<ProductionFormStateWithFrontEndOnlyFields>,
      tabId: string
    ) => {
      // Update the form data in the session storage
      setFormDataByTab((prev) => {
        const updatedData = updater(prev[tabId] || {});
        return {
          ...prev,
          [tabId]: updatedData,
        };
      });

      // Also update the current form state
      setSimpleForm((prev) => updater(prev));
    },
    [setFormDataByTab]
  );

  // Set the current active form based on tab ID
  const setActiveForm = useCallback(
    (tabId: string) => {
      const formData = formDataByTab[tabId] || {};
      setSimpleForm(formData);
    },
    [formDataByTab]
  );

  // Clear form data for a specific tab
  const clearFormData = useCallback(
    (tabId: string) => {
      setFormDataByTab((prev) => {
        const newData = { ...prev };
        delete newData[tabId];
        return newData;
      });

      // If this was the current form, reset it
      setSimpleForm({});
    },
    [setFormDataByTab]
  );

  return {
    formDataByTab,
    simpleForm,
    setSimpleForm,
    initializeFormData,
    updateFormField,
    setActiveForm,
    clearFormData,
  };
};
